import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import ProfileAvatar from '../../components/common/ProfileAvatar';
import DeleteAccountModal from '../../components/profile/DeleteAccountModal';
import { ROUTES } from '../../constants/routes';
import { useAuthContext } from '../../context/AuthContext';
import { useProfileContext } from '../../context/ProfileContext';
import { deleteProfile } from '../../services/profile';
import { getProfilePhotoUrl, mapProfileToForm } from '../../utils/profileMappers';
import ProfileField from './ProfileField';

import { AGE_GROUP_OPTIONS, GENDER_OPTIONS } from '../../constants/profileFormOptions';
import { useDistricts } from '../../hooks/useDistricts';

export default function ProfileDetailsPage() {
  const navigate = useNavigate();
  const { logout } = useAuthContext();
  const { profilePhoto: layoutPhoto } = useOutletContext() || {};
  const fileInputRef = useRef(null);
  const {
    user,
    loading,
    saving,
    error,
    saveError,
    saveMessage,
    saveProfile,
    clearSaveFeedback,
  } = useProfileContext();

  const [form, setForm] = useState(null);
  const [savedForm, setSavedForm] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(layoutPhoto || null);
  const [savedProfilePhoto, setSavedProfilePhoto] = useState(layoutPhoto || null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [toast, setToast] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const { districts, loading: districtsLoading } = useDistricts();

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (saveMessage) {
      setToast({ message: saveMessage, type: 'success' });
      clearSaveFeedback();
    } else if (saveError) {
      setToast({ message: saveError, type: 'error' });
      clearSaveFeedback();
    }
  }, [saveMessage, saveError, clearSaveFeedback]);

  useEffect(() => {
    if (!user) return;

    const mapped = mapProfileToForm(user);
    const photo = getProfilePhotoUrl(user);

    setForm(mapped);
    setSavedForm(mapped);
    setProfilePhoto(photo);
    setSavedProfilePhoto(photo);
    setProfileImageFile(null);
  }, [user?.id, user?.updated_at]);

  const hasChanges = useMemo(() => {
    if (!form || !savedForm) return false;
    return JSON.stringify(form) !== JSON.stringify(savedForm) || Boolean(profileImageFile);
  }, [form, savedForm, profileImageFile]);

  const updateField = (field, value) => {
    clearSaveFeedback();
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert('Profile image must be 2 MB or smaller.');
      return;
    }

    clearSaveFeedback();
    setProfileImageFile(file);

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') setProfilePhoto(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form) return;

    try {
      const updatedUser = await saveProfile(form, profileImageFile);
      const nextForm = mapProfileToForm(updatedUser);
      const nextPhoto = getProfilePhotoUrl(updatedUser);

      setForm(nextForm);
      setSavedForm(nextForm);
      setProfilePhoto(nextPhoto);
      setSavedProfilePhoto(nextPhoto);
      setProfileImageFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {
      // toast via saveError
    }
  };

  const handleCancel = () => {
    if (!savedForm) return;
    clearSaveFeedback();
    setForm(savedForm);
    setProfilePhoto(savedProfilePhoto);
    setProfileImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteAccount = async (reason) => {
    setDeletingAccount(true);
    try {
      await deleteProfile(reason);
      setDeleteModalOpen(false);
      logout();
      navigate(ROUTES.HOME, { replace: true });
    } catch (e) {
      setToast({
        message: e?.message || 'Failed to deactivate account. Please try again.',
        type: 'error',
      });
    } finally {
      setDeletingAccount(false);
    }
  };

  return (
    <>
      <DeleteAccountModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteAccount}
        loading={deletingAccount}
      />

      {toast && (
        <div
          role="status"
          className={`fixed left-1/2 top-6 z-[60] -translate-x-1/2 rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-lg ${
            toast.type === 'error' ? 'bg-red-600' : 'bg-[#2e7d32]'
          }`}
        >
          {toast.message}
        </div>
      )}

      <main className="rounded-2xl p-6 sm:p-8">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-[#9369CC] sm:text-[32px]">My Profile</h1>
          <p className="mx-auto mt-2 max-w-xl text-base leading-relaxed text-[#666666]">
            Edit your basic details, profile photo, and contact information here.
          </p>
        </div>

        {loading ? (
          <div className="mt-8 space-y-6">
            {[1, 2, 3].map((item) => (
              <div key={item} className="h-32 animate-pulse rounded-2xl bg-white/70" />
            ))}
          </div>
        ) : error ? (
          <p className="mt-8 text-center text-sm text-red-600">{error}</p>
        ) : form ? (
          <div className="mt-8 space-y-6">
            <section className="rounded-2xl bg-white p-5 shadow-sm sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <ProfileAvatar
                    src={profilePhoto}
                    alt="Profile"
                    className="h-16 w-16 rounded-xl object-cover sm:h-[72px] sm:w-[72px]"
                    iconClassName="h-8 w-8 text-white"
                  />
                  <div>
                    <p className="text-[20px] font-semibold text-[#1B1B1B]">Profile Photo</p>
                    <p className="mt-2 text-[16px] text-[#999999]">
                      Accepts PNG, JPG, or GIF Max size 2 MB
                    </p>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif"
                  className="hidden"
                  onChange={handlePhotoChange}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="shrink-0 rounded-md bg-gradient-to-r from-[#1A87CC] to-[#0040BF] px-5 py-2.5 text-[20px] font-semibold text-white shadow-sm transition hover:opacity-90"
                >
                  Edit Profile
                </button>
              </div>
            </section>

            <section className="rounded-2xl bg-white p-5 shadow-sm sm:p-6">
              <h2 className="text-[20px] font-medium text-[#1B1B1B]">Basic Information</h2>
              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                <ProfileField
                  icon="user"
                  label="Full Name"
                  placeholder="Your full Name"
                  value={form.fullName}
                  onChange={(e) => updateField('fullName', e.target.value)}
                />
                <ProfileField
                  icon="phone"
                  label="Mobile No"
                  placeholder="Mobile No"
                  value={form.mobile}
                  readOnly
                  className="opacity-80"
                />
                <ProfileField
                  icon="mail"
                  label="Email Address"
                  type="email"
                  placeholder="Email Address"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                />
                <ProfileField
                  icon="location"
                  label="District"
                  type="select"
                  value={form.district}
                  onChange={(e) => updateField('district', e.target.value)}
                  disabled={districtsLoading}
                >
                  <option value="">{districtsLoading ? 'Loading districts...' : 'Select district'}</option>
                  {districts.map((district) => (
                    <option key={district.id} value={district.name}>
                      {district.name}
                    </option>
                  ))}
                </ProfileField>
                <ProfileField
                  icon="college"
                  label="College Name"
                  className="md:col-span-2"
                  placeholder="College Name"
                  value={form.college}
                  onChange={(e) => updateField('college', e.target.value)}
                />
              </div>
            </section>

            <section className="rounded-2xl bg-white p-5 shadow-sm sm:p-6">
              <h2 className="text-[20px] font-medium text-[#1B1B1B]">Personal Information</h2>
              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                <ProfileField
                  icon="calendar"
                  label="Date of Birth"
                  type="date"
                  value={form.dateOfBirth}
                  onChange={(e) => updateField('dateOfBirth', e.target.value)}
                />
                <ProfileField
                  icon="gender"
                  label="Gender"
                  type="select"
                  value={form.gender}
                  onChange={(e) => updateField('gender', e.target.value)}
                >
                  <option value="">Select gender</option>
                  {GENDER_OPTIONS.map((gender) => (
                    <option key={gender} value={gender}>
                      {gender}
                    </option>
                  ))}
                </ProfileField>
                <ProfileField
                  icon="ageGroup"
                  label="Age Group"
                  type="select"
                  value={form.ageGroup}
                  onChange={(e) => updateField('ageGroup', e.target.value)}
                >
                  <option value="">Select age group</option>
                  {AGE_GROUP_OPTIONS.map((ageGroup) => (
                    <option key={ageGroup} value={ageGroup}>
                      {ageGroup}
                    </option>
                  ))}
                </ProfileField>
              </div>
            </section>

            <div className="flex flex-col items-center justify-center gap-4 py-6 sm:flex-row sm:gap-8">
              <button
                type="button"
                onClick={handleCancel}
                disabled={!hasChanges || saving}
                className="text-[20px] font-semibold text-[#9369CC] transition hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="rounded-md bg-gradient-to-r from-[#6A74E1] to-[#B539FD] px-8 py-2.5 text-[20px] font-semibold text-white shadow-md transition disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Change'}
              </button>
            </div>

            <section className="rounded-2xl bg-[#FFE9E9] p-5 shadow-sm sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-[20px] font-semibold text-[#FF595E]">Delete Account</p>
                  <p className="mt-1 text-base font-medium text-[#FF595E]">
                    Permanently remove your account &amp; all your content
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setDeleteModalOpen(true)}
                  className="shrink-0 rounded-xl border border-[#1B1B1B] bg-white px-6 py-2.5 text-base font-semibold text-[#1B1B1B] transition hover:bg-[#FFF5F5]"
                >
                  Deactivate
                </button>
              </div>
            </section>
          </div>
        ) : null}
      </main>
    </>
  );
}
